import React, { useCallback, useEffect, useState } from 'react';

import { Icon } from '../../../../components/Icon';
import { colors } from '../../../../styles/tokens';
import { api } from '../../../../services/api';
import { TrainingsService } from '../../../../services/TrainingsService';
import { useAuth } from '../../../../hooks/auth';
import { useToast } from '../../../../hooks/toast';
import { formatName } from '../../../../utils/formatName';
import { formatSplitLabel } from '../../../../utils/formatSplitLabel';
import { IGroup, ITraining } from '../../../../domains/Training';
import noAvatar from '../../../../assets/no-avatar.svg';

import { ReceiptPrint } from './components/ReceiptPrint';

import {
  ChangeStudentButton,
  Container,
  EmptyState,
  ExerciseRow,
  HeaderBrand,
  HeaderGymName,
  KioskHeader,
  PageSubtitle,
  PageTitle,
  PageWrapper,
  PreviewCard,
  PreviewTitle,
  PrintButton,
  ResultAvatar,
  ResultName,
  ResultRow,
  ResultsList,
  ResultUsername,
  SearchInput,
  SearchWrapper,
  SelectedStudentCard,
  SelectedStudentInfo,
  SplitButton,
  SplitPicker,
} from './styles';

interface IStudentResult {
  id: string;
  fullname: string;
  username: string;
  avatar_url: string | undefined;
}

interface IPrintData {
  trainings: ITraining[];
  studentName: string;
  splitLabel: string;
  lastEditedByName?: string | null;
}

const PrintKiosk: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const trainingsService = new TrainingsService();

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [results, setResults] = useState<IStudentResult[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<IStudentResult>();
  const [splits, setSplits] = useState<IGroup[]>([]);
  const [selectedSplitIndex, setSelectedSplitIndex] = useState<number>();
  const [trainings, setTrainings] = useState<ITraining[]>([]);
  const [printing, setPrinting] = useState(false);
  const [printData, setPrintData] = useState<IPrintData>();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchText), 350);
    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (!debouncedSearch || selectedStudent) {
      setResults([]);
      return;
    }

    api
      .get('users/gym', {
        params: {
          page: 1,
          limit: 8,
          search: debouncedSearch,
          permission_code: 'student',
        },
      })
      .then(response => setResults(response.data.data))
      .catch(() => setResults([]));
  }, [debouncedSearch, selectedStudent]);

  const handleSelectStudent = useCallback((student: IStudentResult) => {
    setSelectedStudent(student);
    setSearchText('');
    setResults([]);
    setSelectedSplitIndex(undefined);
    setTrainings([]);

    trainingsService
      .getTrainingSplits(student.id)
      .then(response => setSplits(response.data.groups))
      .catch(() => setSplits([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = useCallback(() => {
    setSelectedStudent(undefined);
    setSplits([]);
    setSelectedSplitIndex(undefined);
    setTrainings([]);
    setSearchText('');
  }, []);

  const handleSelectSplit = useCallback(
    (index: number) => {
      if (!selectedStudent) return;

      setSelectedSplitIndex(index);

      trainingsService
        .getTrainingsByUserIdAndDay({
          user_id: selectedStudent.id,
          day: String(index),
        })
        .then(response => setTrainings(response.data))
        .catch(() => setTrainings([]));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStudent],
  );

  const handlePrint = useCallback(async () => {
    if (
      !selectedStudent ||
      selectedSplitIndex === undefined ||
      printing ||
      trainings.length === 0
    ) {
      return;
    }

    setPrinting(true);

    try {
      await trainingsService.finishKiosk({
        student_user_id: selectedStudent.id,
        split_index: selectedSplitIndex,
        training_label: formatSplitLabel(selectedSplitIndex).title,
      });

      const lastEditedByName = await api
        .get(`trainings/gym/${selectedStudent.id}/expiration`)
        .then(response => response.data.last_edited_by_name)
        .catch(() => null);

      setPrintData({
        trainings,
        studentName: formatName(selectedStudent.fullname),
        splitLabel: formatSplitLabel(selectedSplitIndex).label,
        lastEditedByName,
      });
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao registrar o treino.',
        description: 'Tente novamente.',
      });
    } finally {
      setPrinting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent, selectedSplitIndex, printing, trainings]);

  useEffect(() => {
    if (!printData) return undefined;

    window.print();

    const handleAfterPrint = () => {
      setPrintData(undefined);
      addToast({
        type: 'success',
        title: 'Treino impresso e marcado como concluído!',
      });
      handleReset();
    };

    window.addEventListener('afterprint', handleAfterPrint);
    return () => window.removeEventListener('afterprint', handleAfterPrint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printData]);

  return (
    <PageWrapper>
      <KioskHeader>
        <HeaderGymName>{user?.gym?.name || 'Academia'}</HeaderGymName>
        <HeaderBrand>TrenzoX</HeaderBrand>
      </KioskHeader>

      <Container>
        <div>
          <PageTitle>Totem de Impressão</PageTitle>
          <PageSubtitle>
            Insira seu nome, selecione o treino e imprima sua ficha.
          </PageSubtitle>
        </div>

        {!selectedStudent && (
          <>
            <SearchWrapper>
              <SearchInput
                autoFocus
                placeholder="Buscar aluno pelo nome..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </SearchWrapper>

            {results.length > 0 && (
              <ResultsList>
                {results.map(student => (
                  <ResultRow
                    key={student.id}
                    type="button"
                    onClick={() => handleSelectStudent(student)}
                  >
                    <ResultAvatar
                      src={student.avatar_url || noAvatar}
                      alt={student.fullname}
                    />
                    <div>
                      <ResultName>{student.fullname}</ResultName>
                      <ResultUsername>@{student.username}</ResultUsername>
                    </div>
                  </ResultRow>
                ))}
              </ResultsList>
            )}

            {!!debouncedSearch && results.length === 0 && (
              <EmptyState>Nenhum aluno encontrado.</EmptyState>
            )}
          </>
        )}

        {selectedStudent && (
          <>
            <SelectedStudentCard>
              <SelectedStudentInfo>
                <ResultAvatar
                  src={selectedStudent.avatar_url || noAvatar}
                  alt={selectedStudent.fullname}
                />
                <span>{formatName(selectedStudent.fullname)}</span>
              </SelectedStudentInfo>
              <ChangeStudentButton type="button" onClick={handleReset}>
                Trocar aluno
              </ChangeStudentButton>
            </SelectedStudentCard>

            {splits.length === 0 && (
              <EmptyState>
                Esse aluno ainda não tem nenhum treino cadastrado.
              </EmptyState>
            )}

            {splits.length > 0 && (
              <SplitPicker>
                {splits.map(split => (
                  <SplitButton
                    key={split.index}
                    type="button"
                    active={split.index === selectedSplitIndex}
                    onClick={() => handleSelectSplit(split.index)}
                  >
                    {formatSplitLabel(split.index).title}
                  </SplitButton>
                ))}
              </SplitPicker>
            )}

            {selectedSplitIndex !== undefined && trainings.length > 0 && (
              <PreviewCard>
                <PreviewTitle>Pré-visualização</PreviewTitle>
                {trainings.map(training => (
                  <ExerciseRow key={training.id}>
                    <span>{training.exercise.name}</span>
                    <span>
                      {training.sets_min || training.sets_max
                        ? `${training.sets_min ?? training.sets_max}-${training.sets_max ?? training.sets_min
                        } séries`
                        : ''}
                    </span>
                  </ExerciseRow>
                ))}
              </PreviewCard>
            )}

            {selectedSplitIndex !== undefined && trainings.length > 0 && (
              <PrintButton
                type="button"
                onClick={handlePrint}
                disabled={printing}
              >
                <Icon name="print" size={20} color={colors.onPrimary} />
                {printing ? 'Registrando...' : 'Imprimir e concluir treino'}
              </PrintButton>
            )}
          </>
        )}

        {printData && (
          <ReceiptPrint
            gymName={user?.gym?.name}
            studentName={printData.studentName}
            splitLabel={printData.splitLabel}
            trainings={printData.trainings}
            lastEditedByName={printData.lastEditedByName}
          />
        )}
      </Container>
    </PageWrapper>
  );
};

export { PrintKiosk };
